import React, { useRef, useState } from 'react';
import { Upload, FileText, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { uploadScript } from '@/services/scriptService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

export type ScriptUploadResult = {
  session_id: string;
  script_name: string;
  gcs_path?: string;
  canonical_gcs_path?: string;
};

type ScriptUploadDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadSuccess: (result: ScriptUploadResult) => void;
  projectId?: string;
};

function ScriptUploadDialog({
  open,
  onOpenChange,
  onUploadSuccess,
  projectId,
}: ScriptUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [scriptName, setScriptName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setSelectedFile(null);
    setScriptName('');
    setError(null);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (next: boolean) => {
    if (uploading) return;
    if (!next) resetForm();
    onOpenChange(next);
  };

  const pickFile = (file: File) => {
    setSelectedFile(file);
    if (!scriptName) {
      setScriptName(file.name.replace(/\.[^/.]+$/, ''));
    }
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      setError(null);
      setUploading(true);
      const finalName = scriptName.trim() || selectedFile.name.replace(/\.[^/.]+$/, '');
      const response = await uploadScript(selectedFile, finalName, ac.signal, projectId);
      if (response.status === 'error') {
        throw new Error(response.error || 'Upload failed');
      }
      onUploadSuccess({
        session_id: response.session_id,
        script_name: finalName,
        gcs_path: response.canonical_gcs_path || response.gcs_path,
        canonical_gcs_path: response.canonical_gcs_path,
      });
      resetForm();
      onOpenChange(false);
    } catch (err: any) {
      if (ac.signal.aborted) return;
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#141414] border-[#2a2a2a] text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-serif font-bold text-white">Upload Script</DialogTitle>
          <DialogDescription className="text-white/60 font-sans">
            Upload your screenplay file. Analysis starts separately from the Analyse step.
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center transition-colors',
            isDragging ? 'border-gold-button bg-gold-button/5' : 'border-[#2a2a2a]',
            error ? 'border-red-500/50' : '',
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            const files = Array.from(e.dataTransfer.files);
            if (files[0]) pickFile(files[0]);
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.docx,.pdf,.fountain,.fdx"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) pickFile(f);
            }}
            className="hidden"
          />
          {selectedFile ? (
            <div className="space-y-3 w-full">
              <FileText className="h-10 w-10 mx-auto text-gold-button" />
              <p className="text-sm font-medium font-sans">{selectedFile.name}</p>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-400 hover:text-red-300 font-sans"
                onClick={() => {
                  setSelectedFile(null);
                  setScriptName('');
                }}
                disabled={uploading}
              >
                <X className="mr-1 h-4 w-4" />
                Remove
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Upload className="h-10 w-10 mx-auto text-gold-button" />
              <p className="text-sm font-sans text-white/80">
                Drag and drop or click to browse
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-gold-button text-neutral-700 hover:bg-gold-button-dark font-sans"
              >
                Select Script File
              </Button>
            </div>
          )}
        </div>

        {selectedFile && (
          <div className="space-y-2">
            <label htmlFor="wf-script-name" className="text-sm font-medium font-sans text-white/80">
              Script Name
            </label>
            <input
              id="wf-script-name"
              type="text"
              value={scriptName}
              onChange={(e) => setScriptName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-[#2a2a2a] rounded-sm bg-black text-white font-sans focus:outline-none focus:ring-2 focus:ring-gold-button"
              disabled={uploading}
            />
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm font-sans">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={uploading}
            className="border-[#2a2a2a] text-white hover:bg-[#1a1a1a] font-sans"
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleUpload()}
            disabled={!selectedFile || uploading}
            className="bg-gold-button text-neutral-700 hover:bg-gold-button-dark font-sans"
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ScriptUploadDialog;
